import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, Plus, Copy, Trash2, ExternalLink, Loader2, RefreshCw } from "lucide-react";

export default function AdminClubs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", code: "", description: "" });
  const [creating, setCreating] = useState(false);
  const origin = window.location.origin;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/clubs"); setItems(data || []); }
    catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nom requis."); return; }
    setCreating(true);
    try {
      await api.post("/admin/clubs", form);
      toast.success("Club / CSE créé.");
      setForm({ name: "", code: "", description: "" });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur."); }
    finally { setCreating(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer « ${c.name} » ? Les membres déjà inscrits ne sont pas supprimés.`)) return;
    try { await api.delete(`/admin/clubs/${c.id}`); toast.success("Supprimé."); await load(); }
    catch { toast.error("Erreur."); }
  };

  const toggleActive = async (c) => {
    try { await api.patch(`/admin/clubs/${c.id}`, { name: c.name, code: c.code, description: c.description, logo: c.logo, active: !c.active }); await load(); }
    catch { toast.error("Erreur."); }
  };

  const copyLink = (c) => {
    const url = `${origin}/club/${c.code}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Lien copié : " + url)).catch(() => toast.message(url));
  };

  return (
    <div data-testid="admin-clubs-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2"><Users className="text-[#E05638]" size={26}/> Clubs & CSE</h1>
          <p className="text-slate-500 text-sm mt-1">Créez une page et un lien dédiés à chaque club/CSE pour amener gratuitement leurs membres sur PROXIreduc-28.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Actualiser</Button>
      </div>

      <form onSubmit={create} className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 grid md:grid-cols-3 gap-3 items-end" data-testid="admin-clubs-form">
        <div><Label>Nom du club / CSE</Label><Input value={form.name} onChange={(e)=>setForm((f)=>({...f, name: e.target.value}))} placeholder="Ex : C'Chartres Volley" data-testid="admin-clubs-name"/></div>
        <div><Label>Code (optionnel)</Label><Input value={form.code} onChange={(e)=>setForm((f)=>({...f, code: e.target.value}))} placeholder="auto depuis le nom" data-testid="admin-clubs-code"/></div>
        <div className="md:col-span-1"><Button type="submit" disabled={creating} className="w-full btn-shine text-white h-10" data-testid="admin-clubs-create"><Plus size={15} className="mr-1"/>{creating ? "Création…" : "Créer"}</Button></div>
        <div className="md:col-span-3"><Label>Description (affichée sur la page)</Label><Textarea rows={2} value={form.description} onChange={(e)=>setForm((f)=>({...f, description: e.target.value}))} data-testid="admin-clubs-desc"/></div>
      </form>

      {loading ? (
        <div className="mt-6 text-slate-500 text-sm"><Loader2 className="animate-spin inline" size={16}/> Chargement…</div>
      ) : items.length === 0 ? (
        <div className="mt-6 text-slate-500 text-sm bg-white border border-dashed rounded-2xl p-8 text-center">Aucun club/CSE pour l'instant.</div>
      ) : (
        <div className="mt-5 space-y-3">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-club-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900">{c.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{c.active ? "Actif" : "Inactif"}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">/club/{c.code} · <b className="text-slate-700">{c.member_count}</b> membre(s) inscrit(s)</div>
              </div>
              <div className="flex items-center gap-2">
                <a href={`/club/${c.code}`} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 inline-flex items-center gap-1" data-testid={`admin-club-open-${c.id}`}>Voir <ExternalLink size={12}/></a>
                <Button size="sm" variant="outline" onClick={()=>copyLink(c)} data-testid={`admin-club-copy-${c.id}`}><Copy size={13} className="mr-1"/>Copier le lien</Button>
                <Button size="sm" variant="outline" onClick={()=>toggleActive(c)} data-testid={`admin-club-toggle-${c.id}`}>{c.active ? "Désactiver" : "Activer"}</Button>
                <Button size="sm" variant="outline" onClick={()=>remove(c)} className="text-rose-600 hover:bg-rose-50" data-testid={`admin-club-delete-${c.id}`}><Trash2 size={13}/></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
